import { kpLiveApi } from '@/services/kursi-play-v2/kpLiveApiClient';
import { supabase } from '@/utils/supabase';
import type { KpQualityTier, KpV2LessonWithStatus } from '@/types/kursi-play-v2';

export type PracticeMode = 'weakness' | 'topic' | 'quick' | 'mistakes';

export type PracticeCenterData = {
  worldId: string;
  worldName: string;
  lessons: KpV2LessonWithStatus[];
  weakLessonIds: string[];
  recentMistakeLessonIds: string[];
  recentMistakeScenarioIds: string[];
  hasAttemptHistory: boolean;
  hasMistakes: boolean;
};

type AttemptRow = {
  scenario_id: string;
  lesson_id: string;
  quality_tier: KpQualityTier;
  created_at: string;
};

const QUALITY_SCORE: Record<KpQualityTier, number> = {
  strong: 4,
  acceptable: 3,
  weak: 2,
  risky: 1,
};

export async function loadPracticeCenterData(slug: string, userId: string): Promise<PracticeCenterData> {
  const home = await kpLiveApi.getWorldHome(slug, userId);

  const base: PracticeCenterData = {
    worldId: home.world.id,
    worldName: home.world.world_name,
    lessons: home.lessons,
    weakLessonIds: [],
    recentMistakeLessonIds: [],
    recentMistakeScenarioIds: [],
    hasAttemptHistory: false,
    hasMistakes: false,
  };

  if (!userId || userId.startsWith('guest_')) {
    return base;
  }

  const { data: attempts } = await supabase
    .from('kp_user_scenario_attempts')
    .select('scenario_id, lesson_id, quality_tier, created_at')
    .eq('user_id', userId)
    .eq('world_id', home.world.id)
    .order('created_at', { ascending: false })
    .limit(500);

  const rows = (attempts ?? []) as AttemptRow[];
  if (rows.length === 0) return base;

  const lessonStats = new Map<string, { total: number; score: number; lastAt: string }>();
  for (const row of rows) {
    const prev = lessonStats.get(row.lesson_id) ?? { total: 0, score: 0, lastAt: row.created_at };
    lessonStats.set(row.lesson_id, {
      total: prev.total + 1,
      score: prev.score + (QUALITY_SCORE[row.quality_tier] ?? 2),
      lastAt: prev.lastAt > row.created_at ? prev.lastAt : row.created_at,
    });
  }

  const weakSorted = [...home.lessons]
    .sort((a, b) => {
      const sa = lessonStats.get(a.id);
      const sb = lessonStats.get(b.id);
      if (!sa && !sb) return a.order_index - b.order_index;
      if (!sa) return 1;
      if (!sb) return -1;
      const avgA = sa.score / sa.total;
      const avgB = sb.score / sb.total;
      if (avgA !== avgB) return avgA - avgB;
      return sa.lastAt > sb.lastAt ? -1 : 1;
    })
    .map((l) => l.id);

  const mistakes = rows.filter((r) => r.quality_tier === 'weak' || r.quality_tier === 'risky');
  const recentMistakeLessonIds = Array.from(new Set(mistakes.map((m) => m.lesson_id)));
  const recentMistakeScenarioIds = Array.from(new Set(mistakes.map((m) => m.scenario_id)));

  return {
    ...base,
    weakLessonIds: weakSorted,
    recentMistakeLessonIds,
    recentMistakeScenarioIds,
    hasAttemptHistory: true,
    hasMistakes: mistakes.length > 0,
  };
}

export function pickQuickLessons(lessons: KpV2LessonWithStatus[], targetInteractions: number): string[] {
  const unlocked = lessons.filter((l) => l.status !== 'locked').sort((a, b) => {
    if (a.status === 'current' && b.status !== 'current') return -1;
    if (b.status === 'current' && a.status !== 'current') return 1;
    return a.order_index - b.order_index;
  });

  const picked: string[] = [];
  let count = 0;

  for (const lesson of unlocked) {
    picked.push(lesson.id);
    count += Math.max(1, lesson.scenario_count || 0);
    if (count >= targetInteractions) break;
  }

  return picked;
}
